import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Target, Eye, Heart, Leaf, Globe, Users } from "lucide-react";

// Alluz Logo Component
const AlluzLogo = ({ className = "", size = 40 }) => (
  <img 
    src="/logo-alluz.png" 
    alt="Alluz" 
    className={className}
    style={{ width: size, height: size, objectFit: 'contain' }}
  />
);

export default function MarqueeSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const values = [
    "Sustentabilidade",
    "Inovação", 
    "Transparência",
    "Compromisso",
    "Excelência",
    "Humanização",
    "Tecnologia",
    "Confiança",
  ];

  return (
    <section
      id="missao"
      ref={ref}
      className="py-20 sm:py-24 bg-gradient-to-b from-[#92400E] via-[#78350F] to-[#92400E] relative overflow-hidden"
      data-testid="marquee-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,180,20,0.5) 1px, transparent 0)`,
            backgroundSize: "50px 50px",
          }}
        />
      </div>

      {/* Glowing Orbs */}
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-[#FFB414]/10 blur-[100px] animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-[#FFC940]/10 blur-[100px] animate-pulse" style={{ animationDelay: '2s' }} />

      {/* Floating Logos Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute opacity-5"
            style={{
              left: `${10 + i * 20}%`,
              top: `${15 + (i % 3) * 25}%`,
            }}
            animate={{
              y: [0, -20, 0],
              rotate: [0, 5, 0],
            }}
            transition={{
              duration: 8 + i * 2,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.5,
            }}
          >
            <AlluzLogo size={60 + i * 15} />
          </motion.div>
        ))}
      </div>

      <div className="container mx-auto px-4 sm:px-6 max-w-6xl relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          <motion.div 
            className="flex items-center justify-center gap-4 mb-5"
            whileHover={{ scale: 1.05 }}
          >
            <AlluzLogo size={55} className="logo-glow" />
          </motion.div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black font-['Outfit'] text-white mb-4">
            Nossa <span className="gradient-text">Essência</span>
          </h2>
          <p className="text-base sm:text-lg text-white/60 max-w-2xl mx-auto px-4">
            Os pilares que guiam a Alluz em direção a um futuro mais brilhante.
          </p>
        </motion.div>

        {/* Mission, Vision, Values Cards */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 mb-16"
        >
          {/* Missão */}
          <motion.div 
            className="group relative bg-gradient-to-br from-[#FFB414]/20 to-[#FF8C00]/10 backdrop-blur-xl rounded-3xl p-6 sm:p-8 border border-[#FFB414]/30 hover:border-[#FFB414]/60 transition-all duration-500 overflow-hidden"
            whileHover={{ y: -8, scale: 1.02 }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#FFB414]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            
            <div className="relative z-10">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FFB414] to-[#FF8C00] flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-500 shadow-lg shadow-[#FFB414]/30">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold font-['Outfit'] text-white mb-3">
                Missão
              </h3>
              <p className="text-white/70 text-sm sm:text-base leading-relaxed">
                Democratizar o acesso à energia solar, combinando{" "}
                <span className="text-[#FFB414] font-semibold">sustentabilidade</span> e{" "}
                <span className="text-[#FFB414] font-semibold">tecnologia</span>.
              </p>
            </div>
          </motion.div>

          {/* Visão */}
          <motion.div 
            className="group relative bg-gradient-to-br from-[#FFB414]/20 to-[#FF8C00]/10 backdrop-blur-xl rounded-3xl p-6 sm:p-8 border border-[#FFB414]/30 hover:border-[#FFB414]/60 transition-all duration-500 overflow-hidden"
            whileHover={{ y: -8, scale: 1.02 }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#FFB414]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            
            <div className="relative z-10">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FFB414] to-[#FF8C00] flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-500 shadow-lg shadow-[#FFB414]/30">
                <Eye className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold font-['Outfit'] text-white mb-3">
                Visão
              </h3>
              <p className="text-white/70 text-sm sm:text-base leading-relaxed">
                Ser a principal referência em energia solar nas{" "}
                <span className="text-[#FFB414] font-semibold">Américas até 2030</span>.
              </p>
            </div>
          </motion.div>

          {/* Valores */}
          <motion.div 
            className="group relative bg-gradient-to-br from-[#FFB414]/20 to-[#FF8C00]/10 backdrop-blur-xl rounded-3xl p-6 sm:p-8 border border-[#FFB414]/30 hover:border-[#FFB414]/60 transition-all duration-500 overflow-hidden"
            whileHover={{ y: -8, scale: 1.02 }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#FFB414]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            
            <div className="relative z-10">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FFB414] to-[#FF8C00] flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-500 shadow-lg shadow-[#FFB414]/30">
                <Heart className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold font-['Outfit'] text-white mb-3">
                Valores
              </h3>
              <p className="text-white/70 text-sm sm:text-base leading-relaxed">
                <span className="text-[#FFB414] font-semibold">Cliente no centro</span>, 
                transparência e compromisso com a sustentabilidade.
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Infinite Marquee */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="relative overflow-hidden py-8"
        >
          {/* Gradient Fades */}
          <div className="absolute left-0 top-0 bottom-0 w-20 sm:w-32 bg-gradient-to-r from-[#92400E] to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-20 sm:w-32 bg-gradient-to-l from-[#92400E] to-transparent z-10" />

          <div className="flex marquee-track">
            {[...values, ...values].map((value, index) => (
              <div
                key={index}
                className="flex items-center gap-6 sm:gap-8 px-6 sm:px-8 whitespace-nowrap"
              >
                <span className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Outfit'] text-white/20 hover:text-[#FFB414] transition-colors duration-300 cursor-default">
                  {value}
                </span>
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-[#FFB414] to-[#FF8C00]" />
              </div>
            ))}
          </div>
        </motion.div>

        {/* Bottom Stats */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-wrap justify-center gap-8 sm:gap-12 mt-12"
        >
          {[
            { icon: Leaf, label: "Eco-friendly", value: "100%" },
            { icon: Globe, label: "Brasil", value: "27 UF" },
            { icon: Users, label: "Clientes", value: "300+" },
          ].map(({ icon: Icon, label, value }, i) => (
            <motion.div 
              key={label} 
              className="flex flex-col items-center gap-2 group"
              whileHover={{ scale: 1.1 }}
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FFB414]/30 to-[#FF8C00]/20 flex items-center justify-center group-hover:from-[#FFB414] group-hover:to-[#FF8C00] transition-all duration-300 border border-[#FFB414]/30">
                <Icon className="w-7 h-7 text-[#FFB414] group-hover:text-white transition-colors" />
              </div>
              <span className="text-xl font-bold text-white">{value}</span>
              <span className="text-xs text-white/50">{label}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
